import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import { visuallyHidden } from "@mui/utils";
import {
  CaseTableActionsToolbarProps,
  CaseTableConfirmToolbarProps,
  CaseTableHeadProps,
  CaseTableOrder,
} from "./types";
import {
  Case,
  CaseOrderBy,
  CaseStatus,
} from "../../../../stores/CaseStore/types";
import useTranslation from "../../../../hooks/useTranslation";
import caseStore from "../../../../stores/CaseStore";
import SearchBar from "../../../../components/SearchBar";
import {
  Alert,
  Button,
  ButtonGroup,
  CircularProgress,
  TextField,
} from "@mui/material";
import { parseDate } from "../../../../utils/helperFunctions";
import { cityMap } from "../../../../utils/cityCodes";
import { headCells } from "./helper";
import feedbackStore from "../../../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../../../stores/FeedbackStore/types";
import "./PendingCases.scss";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { UserRoles } from "../../../../stores/AuthStore/types";
import CaseDetails from "../../../CaseDetails";
import Delegation from "../../../Delegation";
import { observer } from "mobx-react";

const CaseTableHead = (props: CaseTableHeadProps) => {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler =
    (property: keyof Case | null) => (event: React.MouseEvent<unknown>) => {
      property !== null && onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {props.headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align || "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const CaseTableActionsToolbar = (props: CaseTableActionsToolbarProps) => {
  const { t } = useTranslation();
  const { numSelected } = props;

  return (
    <Toolbar
      sx={{
        flexFlow: { xs: "column", sm: "row" },
        gap: { xs: 1, sm: 0 },
        pt: { xs: 1, sm: 0 },
        pb: { xs: 1, sm: 0 },
        pl: { sm: 2 },
        pr: { xs: 2, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity,
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} {t("selected")}
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {t("select-cases-to-process")}
        </Typography>
      )}
      {numSelected > 0 ? (
        <Typography
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 50%" },
            width: { xs: "100%", sm: "auto" },
          }}
          color="inherit"
          bgcolor="white"
          borderRadius="4px"
          variant="subtitle1"
          component="div"
        >
          <ButtonGroup
            fullWidth
            variant="outlined"
            aria-label="outlined primary button group"
          >
            <Button color="success" onClick={props.handleOnApprove}>
              {t("approve")}
            </Button>
            <Button color="error" onClick={props.handleOnReject}>
              {t("reject")}
            </Button>
          </ButtonGroup>
        </Typography>
      ) : (
        <Typography
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 50%" },
            width: { xs: "100%", sm: "auto" },
          }}
          color="inherit"
          variant="subtitle1"
          component="div"
          className="pt"
        >
          <SearchBar handleOnSearch={props.handleOnSearch} />
        </Typography>
      )}
    </Toolbar>
  );
};

const CaseTableConfirmToolbar = (props: CaseTableConfirmToolbarProps) => {
  const { t } = useTranslation();
  const [rejectReason, setRejectReason] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [error, setError] = useState("reject-reason-minlength");

  useEffect(() => {
    setTimeout(() => setDisabled(false), 1000);
  }, []);

  const handleRejectReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRejectReason(e.target.value);

    if (e.target.value.length < 10) {
      setError("reject-reason-minlength");
    } else if (e.target.value.length > 100) {
      setError("reject-reason-maxlength");
    } else {
      setError("");
    }
  };

  return (
    <Toolbar
      sx={{
        flexFlow: { xs: "column", sm: "row" },
        pl: { xs: 2, sm: 2 },
        pr: { xs: 2, sm: 1 },
        pb: { xs: 2, sm: 0 },
        bgcolor: (theme) =>
          alpha(
            theme.palette.primary.main,
            theme.palette.action.activatedOpacity,
          ),
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        color="inherit"
        variant="subtitle1"
        component="div"
      >
        <p>
          {t(
            props.action === "approve"
              ? "approve-selected-cases"
              : "reject-selected-cases",
          )}
        </p>
        {props.action === "approve" ? (
          <ul>
            {props.selected.map((caseId, index) => (
              <li key={caseId}>
                {index}- {caseId}
              </li>
            ))}
          </ul>
        ) : (
          <div className={"mb"}>
            <TextField
              error={!!error}
              onChange={handleRejectReasonChange}
              size={"small"}
              style={{ width: "300px" }}
            />
            {error !== "" && (
              <div className={"pt"}>
                <Alert
                  severity={"error"}
                  sx={{
                    width: { xs: "100%", sm: "268px" },
                    boxSizing: "border-box",
                  }}
                >
                  {t(error)}
                </Alert>
              </div>
            )}
          </div>
        )}
      </Typography>

      <Typography
        sx={{
          flex: { xs: "1 1 100%", sm: "1 1 50%" },
          width: { xs: "100%", sm: "auto" },
        }}
        color="inherit"
        bgcolor="white"
        borderRadius="4px"
        variant="subtitle1"
        component="div"
      >
        <ButtonGroup
          fullWidth
          variant="outlined"
          aria-label="outlined primary button group"
        >
          {props.action === "reject" ? (
            <Button
              disabled={error !== ""}
              color={"error"}
              onClick={() => props.handleOnConfirm("confirm", rejectReason)}
            >
              {t("reject")}
            </Button>
          ) : (
            <Button
              disabled={disabled}
              color={"primary"}
              onClick={() => props.handleOnConfirm("confirm")}
            >
              {t("approve")}
            </Button>
          )}
          <Button
            color={"secondary"}
            onClick={() => props.handleOnConfirm("abort")}
          >
            {t("abort")}
          </Button>
        </ButtonGroup>
      </Typography>
    </Toolbar>
  );
};

const PendingCases = observer(() => {
  const { t } = useTranslation();
  const [order, setOrder] = React.useState<CaseTableOrder>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Case>("id");
  const [selected, setSelected] = React.useState<string[]>([]);
  const [selectedCase, setSelectedCase] = useState<Case>();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [keyword, setKeyword] = useState("");
  const [rows, setRows] = useState<Case[]>([]);
  const [action, setAction] = useState<"approve" | "reject" | null>(null);
  const [loading, setLoading] = useState(false);
  const [openCaseDetails, setOpenCaseDetails] = useState(false);
  const [rowsCount, setRowsCount] = useState(0);

  const fetchRows = useCallback(() => {
    setLoading(true);
    caseStore.setSearchKeyword(keyword);
    caseStore
      .searchCases(
        `${orderBy}${order.charAt(0).toUpperCase()}${order.slice(
          1,
        )}` as CaseOrderBy,
        page,
        rowsPerPage,
        CaseStatus.PENDING,
      )
      .then((result) => {
        setRows(result.cases);
        setRowsCount(result.count);
      })
      .catch((error) => {
        console.error(error);
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orderBy, order, page, rowsPerPage, keyword]);

  useEffect(() => {
    fetchRows();
  }, [orderBy, order, page, rowsPerPage, keyword, fetchRows]);

  useEffect(() => {
    if (
      rows
        .map((row) => row.id)
        .some((id) =>
          caseStore.updatedCases.map((item) => item.id).includes(id),
        )
    ) {
      const updatedRows: Case[] = [];

      rows.forEach((row) => {
        const updatedCase = caseStore.updatedCases.find(
          (item) => item.id === row.id,
        );
        if (!updatedCase || updatedCase.status === CaseStatus.PENDING) {
          updatedRows.push(row);
        }
      });

      const diff = rows.length - updatedRows.length;

      setRows(updatedRows);
      setRowsCount(rowsCount - diff);
    }
  }, [caseStore.updatedCases]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Case,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchKeywordChange = (keyword: string) => {
    setKeyword(keyword);
  };

  const handleOnApprove = () => {
    setAction("approve");
  };

  const handleOnReject = () => {
    setAction("reject");
  };

  const handleReview = (e: React.MouseEvent, caseItem: Case) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCase(caseItem);
    setOpenCaseDetails(true);
  };

  const handleAfterCaseUpdate = (data: Case, isSuccessful: boolean) => {
    if (isSuccessful) {
      setOpenCaseDetails(false);

      const updatedRows = rows.map(
        (row): Case =>
          row.id === data.id
            ? {
                ...row,
                ...data,
              }
            : row,
      );
      setRows(updatedRows);
    }
  };

  const handleConfirm = (
    confirm: "confirm" | "abort",
    rejectReason?: string,
  ) => {
    if (confirm === "abort") {
      setAction(null);
      setSelected([]);
    } else if (confirm === "confirm") {
      setLoading(true);
      const status =
        action === "approve" ? CaseStatus.ACTIVE : CaseStatus.REJECTED;
      caseStore
        .updateStatus(selected, status, rejectReason)
        .then(() => {
          feedbackStore.setShowFeedback(true, {
            severity: FeedbackSeverity.SUCCESS,
            message: "success",
          });
        })
        .catch((error) => {
          console.error(error);
          feedbackStore.setShowFeedback(true, {
            severity: FeedbackSeverity.ERROR,
            message: "",
          });
        })
        .finally(() => {
          setAction(null);
          setSelected([]);
          fetchRows();
        });
    }
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: "100%" }} style={{ position: "relative" }}>
      {loading && (
        <div className="tableShadow">
          <CircularProgress />
        </div>
      )}
      <Paper sx={{ width: "100%", mb: 2 }}>
        {action === null ? (
          <CaseTableActionsToolbar
            numSelected={selected.length}
            handleOnSearch={handleSearchKeywordChange}
            handleOnApprove={handleOnApprove}
            handleOnReject={handleOnReject}
          />
        ) : (
          <CaseTableConfirmToolbar
            action={action}
            selected={selected}
            handleOnConfirm={handleConfirm}
          />
        )}
        <TableContainer style={{ position: "relative" }}>
          {action !== null && <div className="tableShadow"></div>}
          <Table
            sx={{ minWidth: 600 }}
            aria-labelledby="tableTitle"
            size={"medium"}
          >
            <CaseTableHead
              headCells={headCells(t)}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {rows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `cases-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, row.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          "aria-labelledby": labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {row.id}
                    </TableCell>
                    <TableCell align="center">
                      <span className="pr mr hidden-sm">
                        {t(row.ownerRole.toLowerCase())}
                      </span>
                      <span className="hidden-lg">
                        {t(row.ownerRole.toLowerCase())}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      <span className="pr mr hidden-sm">
                        {cityMap[row.caseProvince]}
                      </span>
                      <span className="hidden-lg">
                        {cityMap[row.caseProvince]}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      {parseDate(row.createDate)}
                    </TableCell>
                    <TableCell align={"right"}>
                      <Button
                        variant={"contained"}
                        onClick={(e) => handleReview(e, row)}
                      >
                        <i
                          className={"fal fa-file-alt"}
                          style={{ fontSize: "18px" }}
                        />
                        &nbsp;&nbsp;
                        <i
                          className={"fal fa-search"}
                          style={{ fontSize: "18px" }}
                        />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {action === null && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage={t("rows-per-page")}
            component="div"
            count={rowsCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
      <Dialog
        open={openCaseDetails}
        onClose={() => setOpenCaseDetails(false)}
        className="adminCaseItemDialog"
      >
        <DialogActions className="dialogActions">
          <Button onClick={() => setOpenCaseDetails(false)} color={"error"}>
            &times;
          </Button>
        </DialogActions>
        <DialogContent>
          {selectedCase?.ownerRole === UserRoles.USER ? (
            <CaseDetails
              caseItem={selectedCase}
              reviewMode={true}
              afterSubmit={handleAfterCaseUpdate}
            />
          ) : (
            <Delegation
              caseItem={selectedCase}
              reviewMode={true}
              afterSubmit={handleAfterCaseUpdate}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default PendingCases;
